import type { NextRequest } from "next/server";
import type { UserManager } from "oidc-client-ts";
import { AUTH_COOKIES } from "@/constants/middleware";
import type { RealmSliceState } from "@/store/realm-slice";
import { stripLanguagePrefix } from "@/utils/language";

interface ControlledEnvironment {
  environmentType?: {
    Name?: string | null;
  };
  fullProfile?: boolean | null;
}

export function getSignOutRedirectUri(
  environment: ControlledEnvironment | null | undefined,
  origin?: string
) {
  if (!environment) {
    return;
  }

  const isControlled = environment.environmentType?.Name === "CONTROLLED";
  if (!isControlled) {
    return;
  }

  const resolvedOrigin =
    origin ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  if (!resolvedOrigin) {
    return;
  }

  const variant = environment.fullProfile ? "standard" : "basic";
  return `${resolvedOrigin}/auth/login/controlled/${variant}`;
}

export async function getUserName(
  userManager: UserManager | undefined,
  defaultName = "usuario"
): Promise<string> {
  if (!userManager) {
    return defaultName;
  }

  try {
    const user = await userManager.getUser();
    if (!user?.profile) {
      return "usuario";
    }

    const profile = user.profile;
    return profile.given_name || profile.preferred_username || defaultName;
  } catch (_error) {
    return defaultName;
  }
}

export async function isAuthenticated(userManager: UserManager | undefined) {
  if (!userManager) {
    return false;
  }
  try {
    const user = await userManager.getUser();

    if (user && !user.expired) {
      return true;
    }

    return false;
  } catch (error) {
    throw new Error(`Error checking authentication: ${error}`);
  }
}

export function shouldChangeRealm(
  currentRealm: RealmSliceState["realm"],
  nextRealm: RealmSliceState["realm"]
) {
  return currentRealm !== nextRealm;
}

export async function isAuthenticatedWithIdentification(
  userManager: UserManager | undefined,
  // In this context `identification` is the number of document (country-dependent)
  // for the user
  identification: string
) {
  try {
    if (!(await isAuthenticated(userManager))) {
      return false;
    }

    const user = await userManager?.getUser();
    // The `identification` seems to be kept in the `preferred_username` field
    return Boolean(user) && user?.profile.preferred_username === identification;
  } catch (error) {
    throw new Error(`Error checking authentication with identification: ${error}`);
  }
}

/**
 * Check if user is authenticated by validating tokens in cookies
 */
export function isAuthenticatedMiddleware(req: NextRequest): boolean {
  const realm = req.cookies.get(AUTH_COOKIES.REALM)?.value;

  let token: string | undefined;

  if (realm === "evcore") {
    token = req.cookies.get(AUTH_COOKIES.EVCORE_TOKEN)?.value;
  } else if (realm === "evbasic") {
    token = req.cookies.get(AUTH_COOKIES.EVBASIC_TOKEN)?.value;
  }

  if (!token) {
    return false;
  }

  // Decode and validate token
  const decoded = decodeJWT(token);
  if (!decoded.isValid) {
    return false;
  }

  // Check if token is expired
  if (decoded.exp) {
    const now = Math.floor(Date.now() / 1000);
    if (now >= decoded.exp) {
      return false;
    }
  }

  return true;
}

/**
 * Decode JWT token and extract expiration
 */
export function decodeJWT(token: string): { exp?: number; isValid: boolean } {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return { isValid: false };
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      exp: payload.exp,
      isValid: true,
    };
  } catch {
    return { isValid: false };
  }
}

// Pre-compiled regex cache for route patterns
const routeRegexCache = new Map<string, RegExp>();

function getCompiledRegex(pattern: string): RegExp {
  if (!routeRegexCache.has(pattern)) {
    const regexPattern = pattern.replace(/\*/g, ".*");
    routeRegexCache.set(pattern, new RegExp(`^${regexPattern}$`));
  }
  const result = routeRegexCache.get(pattern);
  if (!result) {
    throw new Error(`Failed to get compiled regex for pattern: ${pattern}`);
  }
  return result;
}

/**
 * Check if a route requires authentication
 */
export function isProtectedRouteMiddleware(
  pathname: string,
  protectedRoutes: string[] = [],
  publicRoutes: string[] = []
): boolean {
  const cleanPathname = stripLanguagePrefix(pathname);

  // Check if it's explicitly a protected route
  if (
    protectedRoutes.some((route) => {
      return getCompiledRegex(route).test(cleanPathname);
    })
  ) {
    return true;
  }

  // Check if it's explicitly a public route
  if (
    publicRoutes.some((route) => {
      return getCompiledRegex(route).test(cleanPathname);
    })
  ) {
    return false;
  }

  // Default to protected for unknown routes
  return true;
}
